import { UserScope } from '../api/ga/bo/user_scope/user_scope_pb'

// Permission names must stay in sync with internal/domain/permission/permissions.go.
// Only the permissions actually enforced by this service are listed here.
export const Permission = {
  DevToolsApp: 'dev_tools:app',
  DevToolsCasinoAPIKeyManage: 'dev_tools:casino_api_key.manage',
  DevToolsCasinoAPIKeyRead: 'dev_tools:casino_api_key.read',
  DevToolsCasinoAPIKeyTest: 'dev_tools:casino_api_key.test',
  DevToolsCasinoConfigurationRead: 'dev_tools:casino_configuration.read',
  DevToolsCasinoConfigurationManage: 'dev_tools:casino_configuration.manage',
  DevToolsCasinoConfigurationManageTechFields:
    'dev_tools:casino_configuration.manage_tech_fields',
  DevToolsCasinoConfigurationDelete: 'dev_tools:casino_configuration.delete',
  DevToolsCasinoConfigMerge: 'dev_tools:casino_config.merge',
  DevToolsProviderConfigMerge: 'dev_tools:provider_config.merge',
} as const

export type Permission = (typeof Permission)[keyof typeof Permission]

// Derived, lookup-friendly view of UserScope. Rebuilt once per scope update
// instead of scanning the (rather long) permission array on every render.
export interface ScopeIndex {
  permissions: Set<string>
  casinos: Set<string>
  providers: Set<string>
}

export const emptyScopeIndex: ScopeIndex = {
  permissions: new Set<string>(),
  casinos: new Set<string>(),
  providers: new Set<string>(),
}

export function buildScopeIndex(scope?: UserScope): ScopeIndex {
  return {
    permissions: new Set(scope?.permissions ?? []),
    casinos: new Set(scope?.direct?.casinoIds ?? []),
    providers: new Set(scope?.direct?.gameProviderNames ?? []),
  }
}

// Mirrors domain.User.CheckPermission: a permission must be present explicitly,
// there is no is_super_user bypass — a super user simply gets every permission.
export function hasPermission(
  index: ScopeIndex,
  ...permissions: Permission[]
): boolean {
  return permissions.some((p) => index.permissions.has(p))
}

export function hasAllPermissions(
  index: ScopeIndex,
  ...permissions: Permission[]
): boolean {
  return permissions.every((p) => index.permissions.has(p))
}

// Mirrors domain.User.checkArgInCasinos for the casino-scoped permissions
// (dev_tools:casino_api_key.manage / .read).
export function hasCasinoPermission(
  index: ScopeIndex,
  permission: Permission,
  casinoId: string
): boolean {
  return index.permissions.has(permission) && index.casinos.has(casinoId)
}

export function hasProviderAccess(
  index: ScopeIndex,
  providerName: string
): boolean {
  return index.providers.has(providerName)
}